import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Zap, MessageCircle, Check } from 'lucide-react';
import { TrustBar } from '../components/TrustBar';

export function OverthinkingJournalPage() {
  return (
    <div className="bg-white">
      <section className="relative bg-gradient-to-b from-[#FDF8F4] to-white pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-6">
            <Brain className="h-4 w-4" />
            <span>For Overthinkers Who Want Action</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            Turn overthinking
            <span className="block text-coral-500">into a clear plan in 5 minutes</span>
          </h1>

          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Stop getting stuck in your head. Get the decision you've been avoiding onto paper and into action.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white font-semibold text-lg rounded-full hover:bg-slate-800 transition-all shadow-lg"
            >
              Break the overthinking cycle now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>

          <TrustBar className="mb-8" />

          <p className="text-sm text-slate-500">
            Free forever plan available. 3 sessions/week to start taking action.
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-slate-900 mb-4">
              Designed for people who think too much, act too little
            </h2>
            <p className="text-xl text-slate-600">
              3 questions. Structured output. Immediate clarity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: MessageCircle,
                title: 'Quick 3-Sentence Input',
                description: 'Just answer 3 questions about what you\'re stuck on. Takes 2 minutes max.'
              },
              {
                icon: Zap,
                title: 'Instant Structured Response',
                description: 'Get 2 clear options, 3 next steps, and what to think about tomorrow'
              },
              {
                icon: Brain,
                title: 'Pattern Recognition',
                description: 'See what triggers your overthinking and what actually helps you decide'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-3xl p-12 border border-slate-200">
            <h2 className="text-3xl font-heading font-bold text-slate-900 mb-8">
              Situations where overthinking keeps you stuck:
            </h2>

            <div className="space-y-4">
              {[
                'Analyzing every option until you pick none',
                'Knowing what to do but waiting for "the right time"',
                'Second-guessing decisions you\'ve already made',
                'Imagining worst-case scenarios instead of taking action',
                'Comparing yourself to others and feeling paralyzed',
                'Having clarity for 5 minutes then talking yourself out of it'
              ].map((situation, index) => (
                <div key={index} className="flex items-start gap-3 bg-white rounded-xl p-4">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{situation}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-white rounded-xl border-2 border-purple-200">
              <p className="text-lg text-slate-700 italic">
                "I've been thinking about this for 3 months. VZMO helped me make the decision in 10 minutes."
              </p>
              <p className="text-sm text-slate-500 mt-2">- Sarah, Product Designer</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
            Your first clear decision in 30 seconds
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Free to start. No credit card. Stop overthinking, start deciding.
          </p>

          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-white text-slate-900 rounded-full font-semibold text-lg hover:bg-slate-100 transition-all shadow-lg"
          >
            Break the overthinking cycle
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
